export const PLATFORM_FEES: Record<string, number> = { direct: 0, workana: 20, other: 0 };

export type PricingMode = "hourly" | "fixed" | "recurring";

export const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export function platformFeeRate(platform: string, customFee: number): number {
  return platform === "other" ? customFee / 100 : (PLATFORM_FEES[platform] ?? 0) / 100;
}

// ─── Package (line items) ────────────────────────────────────────────────────
// A proposal is a list of items; each is priced by hours × rate or a fixed
// price. The optional package discount applies to the items' sum, then the
// platform fee applies to what's left. All money is rounded to cents at each
// step so the numbers the client sees add up line by line.

export interface PackageItemInput {
  pricingMode: PricingMode;
  hours: number;
  hourlyRate: number;
  fixedPrice: number;
  monthlyAmount: number;
}

export interface PackageTotalsInput {
  items: PackageItemInput[];
  discountPercent: number;
  platform: string;
  customFee: number;
}

export interface PackageTotals {
  itemSubtotals: number[];
  itemsGross: number;
  discount: number;
  /** Items' sum minus the discount — what the platform fee is charged on. */
  gross: number;
  feeRate: number;
  fee: number;
  net: number;
  /** Σ hours of the hourly items (drives the "prazo estimado"). */
  totalHours: number;
  /** Σ monthly amount of the recurring items — a separate, ongoing charge, never folded into gross/net. */
  recurringMonthly: number;
}

export function itemSubtotal(item: PackageItemInput): number {
  if (item.pricingMode === "hourly") return round2(item.hours * item.hourlyRate);
  if (item.pricingMode === "recurring") return round2(item.monthlyAmount);
  return round2(item.fixedPrice);
}

/**
 * Recurring items are priced and shown per line (itemSubtotal), but excluded
 * from itemsGross/discount/gross/fee/net — those are "the one-time deal".
 * A monthly fee isn't discounted by the package deal or charged the
 * marketplace platform fee, and must never be folded into the total that
 * auto_create_payment_plan() parcels when the budget is signed.
 */
export function calculatePackageTotals({
  items,
  discountPercent,
  platform,
  customFee,
}: PackageTotalsInput): PackageTotals {
  const itemSubtotals = items.map(itemSubtotal);
  const oneTimeItems = items.filter((i) => i.pricingMode !== "recurring");
  const itemsGross = round2(oneTimeItems.reduce((s, i) => s + itemSubtotal(i), 0));
  const discount = round2(itemsGross * (discountPercent / 100));
  const gross = round2(itemsGross - discount);
  const feeRate = platformFeeRate(platform, customFee);
  const fee = round2(gross * feeRate);
  const net = round2(gross - fee);
  const totalHours = items.reduce((s, i) => s + (i.pricingMode === "hourly" ? i.hours : 0), 0);
  const recurringMonthly = round2(
    items.reduce((s, i) => s + (i.pricingMode === "recurring" ? i.monthlyAmount : 0), 0),
  );
  return {
    itemSubtotals,
    itemsGross,
    discount,
    gross,
    feeRate,
    fee,
    net,
    totalHours,
    recurringMonthly,
  };
}

// ─── Single line (legacy shape) ──────────────────────────────────────────────

export interface BudgetTotalsInput {
  pricingMode: PricingMode;
  hours: number;
  hourlyRate: number;
  fixedPrice: number;
  platform: string;
  customFee: number;
}

export interface BudgetTotals {
  feeRate: number;
  gross: number;
  fee: number;
  net: number;
}

/** One-item wrapper kept for callers that still price a single line. */
export function calculateBudgetTotals({
  pricingMode,
  hours,
  hourlyRate,
  fixedPrice,
  platform,
  customFee,
}: BudgetTotalsInput): BudgetTotals {
  const { feeRate, gross, fee, net } = calculatePackageTotals({
    items: [{ pricingMode, hours, hourlyRate, fixedPrice, monthlyAmount: 0 }],
    discountPercent: 0,
    platform,
    customFee,
  });
  return { feeRate, gross, fee, net };
}
